import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { LoadingProvider } from './providers/LoadingContext';

import GlobalLoader from './components/GlobalLoader';

import SearchOrderPage from './pages/SearchOrderPage';
import OrderPage from './pages/OrderPage';

import axiosInstance from './utils/axios';
import partsvuTheme from './tenants/partsvu';
import { setTenantConfig } from './tenants/runtimeConfig';

import './assets/scss/global.scss';

// --- Tenant resolution -----------------------------------------------------
// The embedding store loads this app in an iframe with ?shop=<domain>. Capture
// it as early as possible and persist it for the session: utils/axios.js reads
// it from sessionStorage and appends it to every API request so the backend
// tenant middleware can resolve the store. In-iframe navigations (e.g. to the
// order detail page) may drop the query string, so only overwrite when a shop
// is actually present — the persisted value carries the session.
// sessionStorage can throw a SecurityError in a partitioned/blocked third-party
// iframe (some browsers deny storage to cross-site frames). Guard every access
// so a throw here can't blank the whole app at module load.
const safeSessionGet = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable in this context — the shop still rides the URL param */
  }
};

const shopFromUrl = new URLSearchParams(window.location.search).get('shop');

if (shopFromUrl) {
  safeSessionSet('shop', shopFromUrl);
}

const shop = shopFromUrl || safeSessionGet('shop');

// --- Theming ---------------------------------------------------------------
// Each `colors` key maps to a `--color-<key>` CSS custom property consumed by
// the SCSS. The SCSS also carries the PartsVu hex as an inline var() fallback,
// so the UI renders correctly even before — or entirely without — this running.
const applyThemeColors = (colors = {}) => {
  const root = document.documentElement;

  Object.entries(colors).forEach(([key, value]) => {
    if (value) {
      root.style.setProperty(`--color-${key}`, value);
    }
  });
};

// Apply PartsVu defaults synchronously so the :root vars exist immediately.
// This preserves the SCSS-fallback parity guarantee: PartsVu renders correctly
// before — or entirely without — the tenant-config fetch.
applyThemeColors(partsvuTheme.colors);

// Applies a tenant's public config: colors overlaid on the PartsVu defaults (so
// any color a tenant omits falls back to a known-good PartsVu value) plus the
// document title. Called from the sessionStorage cache (synchronously) and from
// the tenant-config fetch.
const applyTenantConfig = (config = {}) => {
  applyThemeColors({ ...partsvuTheme.colors, ...(config.colors || {}) });

  if (config.displayName) {
    document.title = `${config.displayName} Order Status`;
  }
};

// Per-shop cache of the tenant's public config. A returning visit paints the
// tenant palette on the very first frame; only a shop's FIRST-ever load waits on
// the network (and only up to a hard cap), so the branded button never flashes
// the PartsVu default color first.
const cacheKey = shop ? `tenantTheme:${shop}` : null;

let cachedConfig = null;

if (cacheKey) {
  const rawCache = safeSessionGet(cacheKey);

  if (rawCache) {
    try {
      cachedConfig = JSON.parse(rawCache);
    } catch {
      cachedConfig = null;
    }
  }
}

if (cachedConfig) {
  applyTenantConfig(cachedConfig);
  // Seed the Need-help links from the cache so a returning visit has them on the
  // first frame; a missing/partial `links` merges over the defaults.
  setTenantConfig({ links: cachedConfig.links });
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Render exactly once, whichever path (cache hit, fetch resolve, or timeout cap)
// reaches it first.
let hasRendered = false;

const renderApp = () => {
  if (hasRendered) {
    return;
  }

  hasRendered = true;

  root.render(
    <React.StrictMode>
      <LoadingProvider>
        <SnackbarProvider maxSnack={3}>
          <Router>
            <GlobalLoader />
            <Routes>
              <Route path="/" element={<SearchOrderPage />} />
              <Route path="/pages/advanced-order-status/:orderId" element={<OrderPage />} />
            </Routes>
          </Router>
        </SnackbarProvider>
      </LoadingProvider>
    </React.StrictMode>
  );
};

// Fetch the tenant's public config, apply it, and refresh the per-shop cache.
const fetchTenantConfig = () =>
  axiosInstance
    .get('/api/order/tenant-config')
    .then((response) => {
      const config = response?.data || {};

      applyTenantConfig(config);
      setTenantConfig({ links: config.links });

      if (cacheKey) {
        safeSessionSet(cacheKey, JSON.stringify({
          colors: config.colors || {},
          displayName: config.displayName || '',
          links: config.links || {},
        }));
      }
    });

if (shop && !cachedConfig) {
  // First-ever load for this shop with no cached palette: hold the first paint
  // until the tenant config resolves, capped at 1500ms so a slow or failed
  // config endpoint can never block the app (order lookups work via the shop
  // param regardless). The PartsVu defaults are already applied as a fallback.
  const cap = setTimeout(renderApp, 1500);

  fetchTenantConfig()
    .catch(() => {
      // Config endpoint unreachable (or unknown shop): keep the PartsVu defaults.
    })
    .finally(() => {
      clearTimeout(cap);
      renderApp();
    });
} else {
  // Cached palette already applied above, or no shop at all: render immediately.
  renderApp();

  // With a shop, still refresh the cache/colors/title in the background so the
  // next load reflects any config change.
  if (shop) {
    fetchTenantConfig().catch(() => {});
  }
}
